import React, { useMemo, useState } from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useEntities } from "@hooks/useEntities";
import { EntityFilterTrigger } from "./EntityFilterTrigger";
import { EntityFilterPopup, groupEntitiesForFilter } from "./EntityFilterPopup";
import { EntityFilterProps } from "./types";

const NEVER_DISABLED = () => false;

/**
 * Single-entity chooser (`selectionMode="single"`): Business / Personal
 * sections, a pick closes the popup. The trigger shows the picked entity, or
 * every entity in `filter` mode and none in `selection` mode.
 */
export const EntityFilterSingleCombobox: React.FC<EntityFilterProps> = ({
    filters,
    onChange,
    onlyBusiness,
    container,
    showLabel,
    excludeAllMockEntities,
    excludeEmptyMockEntities,
    mode = "filter",
    disabled = false,
    isEntityDisabled = NEVER_DISABLED,
    disabledEntityTooltip,
    triggerButtonProps,
    "aria-labelledby": ariaLabelledby,
    popupLabel,
    open,
    onOpenChange,
    renderTrigger,
    onBlur,
    invalid,
    ref,
}) => {
    const [search, setSearch] = useState("");
    const entities = useEntities({
        excludeEmptyMockEntities,
        excludeMockEntities: excludeAllMockEntities,
    });
    const availableEntities = useMemo(
        () =>
            onlyBusiness
                ? entities.filter((entity) => entity.isBusiness)
                : entities,
        [entities, onlyBusiness],
    );
    const selectedEntity = availableEntities.find(
        (entity) => entity.id === filters.entityIds?.[0],
    );
    let displayedEntities = availableEntities;
    if (selectedEntity) {
        displayedEntities = [selectedEntity];
    } else if (mode === "selection") {
        displayedEntities = [];
    }
    const emptyEntityIds = mode === "selection" ? [] : undefined;
    const { businessEntities, personalEntities } = useMemo(
        () => groupEntitiesForFilter({ entities, onlyBusiness, search }),
        [entities, onlyBusiness, search],
    );

    return (
        <Picker.Select
            searchable
            value={selectedEntity?.id ?? null}
            onValueChange={(entityId) =>
                onChange({
                    entityIds: entityId === null ? emptyEntityIds : [entityId],
                })
            }
            onInputValueChange={setSearch}
            open={open}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    setSearch("");
                }
                onOpenChange?.(isOpen);
            }}
            disabled={disabled}
            aria-invalid={invalid}
        >
            <EntityFilterTrigger
                renderTrigger={renderTrigger}
                selectedEntities={displayedEntities}
                disabled={disabled}
                showLabel={showLabel}
                triggerButtonProps={triggerButtonProps}
                aria-labelledby={ariaLabelledby}
                invalid={invalid}
                ref={ref}
                onBlur={onBlur}
            />
            <EntityFilterPopup
                container={container}
                aria-label={popupLabel}
                groups={[
                    { title: "Business", entities: businessEntities },
                    { title: "Personal", entities: personalEntities },
                ]}
                isEntityDisabled={isEntityDisabled}
                disabledEntityTooltip={disabledEntityTooltip}
                selectedCount={selectedEntity ? 1 : 0}
                onDeselect={() => onChange({ entityIds: emptyEntityIds })}
            />
        </Picker.Select>
    );
};
